import { useEffect, useRef, useState } from 'react';
import {
  Camera,
  ChevronLeft,
  ChevronRight,
  FileText,
  FileUp,
  Images,
  Loader2,
  Save,
  Trash2,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../config/supabase';
import { createMedicalRecord } from '../../services/MedicalRecordService';
import { MedicalRecordFileStorageService } from '../../services/MedicalRecordFileStorageService';
import type { User } from '../../services/UserService';
import { createExamPdfFile } from '../../utils/examDocumentPdf';

const MAX_DOCUMENT_PAGES = 20;
const MAX_PDF_SIZE_BYTES = 10 * 1024 * 1024;
const DOCUMENT_UPLOAD_ROLES = new Set(['admin', 'doctor', 'fisioterapeuta', 'interno']);

interface DocumentUploadProps {
  onUploadComplete?: () => void;
}

interface PatientOption {
  patientId: string;
  patientFullName: string;
  patientCpf?: string;
  patientUserId?: string | null;
  patientLifecycleStatus?: 'active' | 'inactive' | 'archived' | string;
}

interface DocumentImagePage {
  id: string;
  file: File;
  previewUrl: string;
}

interface PatientClientQueryRow {
  patientId: string;
  patients: {
    patientFullName?: string | null;
    patientCpf?: string | null;
    userId?: string | null;
    patientLifecycleStatus?: string | null;
  } | Array<{
    patientFullName?: string | null;
    patientCpf?: string | null;
    userId?: string | null;
    patientLifecycleStatus?: string | null;
  }> | null;
}

interface UserClientQueryRow {
  userId: string;
  userRole: string;
  users: {
    userDisplayName?: string | null;
    userEmail?: string | null;
    firebase_uid?: string | null;
  } | Array<{
    userDisplayName?: string | null;
    userEmail?: string | null;
    firebase_uid?: string | null;
  }> | null;
}

const isPdfFile = (file: File) =>
  file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

const isImageFile = (file: File) =>
  file.type.startsWith('image/') || /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name);

const DocumentUpload = ({ onUploadComplete }: DocumentUploadProps) => {
  const { user, selectedClient } = useAuth();
  const activeClientId = selectedClient?.clientId || user?.current_client_id || null;
  const activeUserRole = selectedClient?.userRole || user?.userRole;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [patients, setPatients] = useState<PatientOption[]>([]);
  const [doctors, setDoctors] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pages, setPages] = useState<DocumentImagePage[]>([]);
  const [selectedPdf, setSelectedPdf] = useState<File | null>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const pagesRef = useRef<DocumentImagePage[]>([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    patientId: '',
    doctorId: '',
    patientName: '',
    doctorName: '',
    type: 'exam',
  });

  useEffect(() => {
    const fetchUsers = async () => {
      if (!user) {
        toast.error('Você precisa estar autenticado');
        setIsLoading(false);
        return;
      }

      try {
        if (!activeClientId) {
          toast.error('Cliente não associado ao usuário atual.');
          setIsLoading(false);
          return;
        }

        if (!activeUserRole || !DOCUMENT_UPLOAD_ROLES.has(activeUserRole)) {
          toast.error('Acesso não autorizado');
          setIsLoading(false);
          return;
        }

        const { data: patientClientsData, error: patientClientsError } = await supabase
          .from('patient_clients')
          .select(`
            patientId,
            patients!inner(
              patientFullName,
              patientCpf,
              userId,
              patientLifecycleStatus
            )
          `)
          .eq('clientId', activeClientId);

        if (patientClientsError) throw patientClientsError;

        const patientRows = (patientClientsData || []) as unknown as PatientClientQueryRow[];
        const patientsList: PatientOption[] = patientRows
          .map((row) => {
            const patient = Array.isArray(row.patients) ? row.patients[0] : row.patients;
            if (!row?.patientId || !patient) return null;
            return {
              patientId: row.patientId,
              patientFullName: patient.patientFullName || 'Paciente',
              patientCpf: patient.patientCpf || '',
              patientUserId: patient.userId || null,
              patientLifecycleStatus: patient.patientLifecycleStatus || 'active',
            } as PatientOption;
          })
          .filter((patient): patient is PatientOption => !!patient)
          .filter((patient) => patient.patientLifecycleStatus !== 'archived');

        const { data: usersData, error: usersError } = await supabase
          .from('user_clients')
          .select('userId, userRole, users(userDisplayName, userEmail, firebase_uid)')
          .eq('clientId', activeClientId)
          .in('userRole', ['doctor', 'fisioterapeuta']);

        if (usersError) throw usersError;

        const doctorsList: User[] = [];
        const userClientRows = (usersData || []) as unknown as UserClientQueryRow[];
        userClientRows.forEach((uc) => {
          const userProfile = Array.isArray(uc.users) ? uc.users[0] : uc.users;
          if (!userProfile) return;
          doctorsList.push({
            userId: uc.userId,
            firebase_uid: userProfile.firebase_uid || '',
            userDisplayName: userProfile.userDisplayName || '',
            userEmail: userProfile.userEmail || '',
            userRole: uc.userRole,
            current_client_id: activeClientId,
          });
        });

        setPatients(patientsList);
        setDoctors(doctorsList);
      } catch (error) {
        console.error('Error fetching users:', error);
        toast.error('Erro ao carregar usuários. Verifique suas permissões.');
      } finally {
        setIsLoading(false);
      }
    };

    void fetchUsers();
  }, [user, activeClientId, activeUserRole]);

  useEffect(() => {
    pagesRef.current = pages;
  }, [pages]);

  useEffect(
    () => () => {
      pagesRef.current.forEach((page) => URL.revokeObjectURL(page.previewUrl));
    },
    [],
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormData((current) => {
      const next = { ...current, [name]: value };

      if (name === 'patientId') {
        next.patientName = patients.find((patient) => patient.patientId === value)?.patientFullName || '';
      }
      if (name === 'doctorId') {
        next.doctorName = doctors.find((doctor) => doctor.userId === value)?.userDisplayName || '';
      }

      return next;
    });
  };

  const clearPages = () => {
    pagesRef.current.forEach((page) => URL.revokeObjectURL(page.previewUrl));
    pagesRef.current = [];
    setPages([]);
  };

  const addImages = (files: File[]) => {
    const imageFiles = files.filter(isImageFile);
    if (imageFiles.length === 0) {
      toast.error('Selecione imagens válidas.');
      return;
    }

    const currentPages = pagesRef.current;
    const availableSlots = MAX_DOCUMENT_PAGES - currentPages.length;
    if (availableSlots <= 0) {
      toast.error(`Cada documento pode ter no máximo ${MAX_DOCUMENT_PAGES} páginas.`);
      return;
    }

    setSelectedPdf(null);
    const nextPages = imageFiles.slice(0, availableSlots).map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    const updatedPages = [...currentPages, ...nextPages];
    pagesRef.current = updatedPages;
    setPages(updatedPages);

    if (imageFiles.length > availableSlots) {
      toast.info(`Foram adicionadas somente ${availableSlots} páginas para respeitar o limite.`);
    }
  };

  const selectPdf = (file?: File) => {
    if (!file) return;
    if (!isPdfFile(file)) {
      toast.error('Selecione um arquivo PDF válido.');
      return;
    }
    if (file.size > MAX_PDF_SIZE_BYTES) {
      toast.error('O PDF deve ter no máximo 10MB.');
      return;
    }

    clearPages();
    setSelectedPdf(file);
  };

  const removePage = (id: string) => {
    setPages((current) => {
      const removed = current.find((page) => page.id === id);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      const updatedPages = current.filter((page) => page.id !== id);
      pagesRef.current = updatedPages;
      return updatedPages;
    });
  };

  const movePage = (index: number, direction: -1 | 1) => {
    setPages((current) => {
      const destination = index + direction;
      if (destination < 0 || destination >= current.length) return current;
      const next = [...current];
      [next[index], next[destination]] = [next[destination], next[index]];
      pagesRef.current = next;
      return next;
    });
  };

  const openCamera = () => cameraInputRef.current?.click();
  const openGallery = () => galleryInputRef.current?.click();

  const resetForm = () => {
    clearPages();
    setSelectedPdf(null);
    setFormData({
      title: '',
      description: '',
      patientId: '',
      doctorId: '',
      patientName: '',
      doctorName: '',
      type: 'exam',
    });
  };

  const saveDocument = async () => {
    if (!user || !selectedClient || !activeClientId) {
      toast.error('Usuário não autenticado ou sem cliente associado.');
      return;
    }
    if (!formData.title.trim() || !formData.patientId || !formData.doctorId) {
      toast.error('Informe o título, o paciente e o profissional responsável.');
      return;
    }
    if (!selectedPdf && pages.length === 0) {
      toast.error('Adicione pelo menos uma foto ou selecione um PDF.');
      return;
    }

    setIsSubmitting(true);
    let uploadedPath = '';

    try {
      const file = selectedPdf || await createExamPdfFile(pages.map((page) => page.file), formData.title);
      if (file.size > MAX_PDF_SIZE_BYTES) {
        throw new Error('O PDF gerado ultrapassou 10MB. Remova algumas páginas ou divida o documento.');
      }

      const patient = patients.find((item) => item.patientId === formData.patientId);
      const upload = await MedicalRecordFileStorageService.uploadFile({
        clientId: activeClientId,
        patientId: formData.patientId,
        patientUserId: patient?.patientUserId,
        file,
        user,
        selectedClient,
      });
      uploadedPath = upload.path;

      try {
        await createMedicalRecord({
          title: formData.title.trim(),
          description: formData.description.trim(),
          patientId: formData.patientId,
          doctorId: formData.doctorId,
          type: formData.type,
          date: new Date(),
          attachments: [{
            name: file.name,
            url: '',
            path: upload.path,
            provider: upload.provider,
            downloadUrl: upload.downloadUrl,
            type: 'pdf',
          }],
          uploadedBy: user.userId,
          uploadedByRole: activeUserRole,
          uploadedByName: user.userDisplayName || '',
          clientId: activeClientId,
        });
      } catch (error) {
        await MedicalRecordFileStorageService.deleteFile(uploadedPath).catch((cleanupError) => {
          console.error('Não foi possível remover o arquivo órfão do documento:', cleanupError);
        });
        throw error;
      }

      resetForm();
      toast.success('Documento adicionado com sucesso!');
      onUploadComplete?.();
    } catch (error) {
      console.error('Error creating medical record:', error);
      toast.error(error instanceof Error ? error.message : 'Não foi possível adicionar o documento.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-sky-700" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="title" className="mb-1 block text-sm font-medium text-slate-700">Título do documento *</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="w-full rounded-lg border-slate-300"
            required
          />
        </div>
        <div>
          <label htmlFor="type" className="mb-1 block text-sm font-medium text-slate-700">Tipo de documento</label>
          <select
            id="type"
            name="type"
            value={formData.type}
            onChange={handleChange}
            className="w-full rounded-lg border-slate-300"
          >
            <option value="exam">Exame</option>
            <option value="prescription">Prescrição</option>
            <option value="report">Relatório</option>
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="description" className="mb-1 block text-sm font-medium text-slate-700">Descrição</label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={2}
          className="w-full rounded-lg border-slate-300"
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="patientId" className="mb-1 block text-sm font-medium text-slate-700">Paciente *</label>
          <select
            id="patientId"
            name="patientId"
            value={formData.patientId}
            onChange={handleChange}
            className="w-full rounded-lg border-slate-300"
            required
          >
            <option value="">Selecione um paciente</option>
            {patients.map((patient) => (
              <option key={patient.patientId} value={patient.patientId}>
                {patient.patientFullName}{patient.patientCpf ? ` (${patient.patientCpf})` : ''}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="doctorId" className="mb-1 block text-sm font-medium text-slate-700">Profissional responsável *</label>
          <select
            id="doctorId"
            name="doctorId"
            value={formData.doctorId}
            onChange={handleChange}
            className="w-full rounded-lg border-slate-300"
            required
          >
            <option value="">Selecione um profissional</option>
            {doctors.map((doctor) => (
              <option key={doctor.userId} value={doctor.userId}>
                {doctor.userDisplayName} ({doctor.userEmail})
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="rounded-xl border border-sky-200 bg-sky-50 px-3 py-2.5 text-sm text-sky-950">
        Fotografe cada página ou selecione várias imagens. As fotos serão reduzidas e reunidas em um único PDF.
      </div>

      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(event) => {
          addImages(Array.from(event.target.files || []));
          event.target.value = '';
        }}
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(event) => {
          addImages(Array.from(event.target.files || []));
          event.target.value = '';
        }}
      />
      <input
        ref={pdfInputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={(event) => {
          selectPdf(event.target.files?.[0]);
          event.target.value = '';
        }}
      />

      <div className="grid gap-2 sm:grid-cols-3">
        <button
          type="button"
          onClick={openCamera}
          disabled={isSubmitting}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-teal-200 bg-teal-50 px-3 py-3 text-sm font-semibold text-teal-800 hover:bg-teal-100 disabled:opacity-50"
        >
          <Camera className="h-4 w-4" /> Fotografar página
        </button>
        <button
          type="button"
          onClick={openGallery}
          disabled={isSubmitting}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-sky-200 bg-sky-50 px-3 py-3 text-sm font-semibold text-sky-800 hover:bg-sky-100 disabled:opacity-50"
        >
          <Images className="h-4 w-4" /> Escolher fotos
        </button>
        <button
          type="button"
          onClick={() => pdfInputRef.current?.click()}
          disabled={isSubmitting}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
        >
          <FileUp className="h-4 w-4" /> PDF pronto
        </button>
      </div>

      <p className="text-xs leading-relaxed text-slate-500">
        No celular, a câmera pode adicionar somente uma página por vez. Repita a captura: as páginas anteriores permanecerão no documento.
      </p>

      {selectedPdf && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <FileText className="h-5 w-5 shrink-0 text-slate-500" />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-800">{selectedPdf.name}</p>
              <p className="text-xs text-slate-500">{(selectedPdf.size / 1024 / 1024).toFixed(1)} MB</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setSelectedPdf(null)}
            className="rounded-lg p-2 text-slate-500 hover:bg-white hover:text-red-600"
            aria-label="Remover PDF selecionado"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )}

      {pages.length > 0 && (
        <div>
          <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-800">Páginas do documento</p>
              <p className="text-xs text-slate-500">{pages.length}/{MAX_DOCUMENT_PAGES} páginas adicionadas</p>
            </div>
            {pages.length < MAX_DOCUMENT_PAGES && (
              <div className="grid grid-cols-2 gap-2 sm:flex">
                <button
                  type="button"
                  onClick={openCamera}
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-2.5 py-2 text-xs font-semibold text-teal-800 hover:bg-teal-100"
                >
                  <Camera className="h-3.5 w-3.5" /> Fotografar próxima
                </button>
                <button
                  type="button"
                  onClick={openGallery}
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-sky-200 bg-sky-50 px-2.5 py-2 text-xs font-semibold text-sky-800 hover:bg-sky-100"
                >
                  <Images className="h-3.5 w-3.5" /> Adicionar fotos
                </button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {pages.map((page, index) => (
              <div key={page.id} className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                <div className="relative aspect-[3/4] bg-slate-100">
                  <img src={page.previewUrl} alt={`Página ${index + 1}`} className="h-full w-full object-contain" />
                  <span className="absolute left-2 top-2 rounded-full bg-slate-900/75 px-2 py-0.5 text-xs font-semibold text-white">
                    {index + 1}
                  </span>
                </div>
                <div className="flex items-center justify-between border-t border-slate-200 p-1.5">
                  <button
                    type="button"
                    onClick={() => movePage(index, -1)}
                    disabled={index === 0}
                    className="rounded p-1 text-slate-500 hover:bg-white disabled:opacity-25"
                    aria-label={`Mover página ${index + 1} para trás`}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => removePage(page.id)}
                    className="rounded p-1 text-slate-500 hover:bg-white hover:text-red-600"
                    aria-label={`Remover página ${index + 1}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => movePage(index, 1)}
                    disabled={index === pages.length - 1}
                    className="rounded p-1 text-slate-500 hover:bg-white disabled:opacity-25"
                    aria-label={`Mover página ${index + 1} para frente`}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => void saveDocument()}
        disabled={isSubmitting}
        className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-sky-800 px-4 py-3 text-sm font-semibold text-white hover:bg-sky-900 disabled:opacity-50"
      >
        {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        {isSubmitting ? 'Preparando e enviando...' : 'Salvar documento'}
      </button>
    </div>
  );
};

export default DocumentUpload;
